import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Shield, AlertTriangle, CheckCircle, XCircle, FileText, Clock, ArrowLeft, ExternalLink, Upload } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import api from '../lib/api';

export default function HMOCompliance() {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [compliance, setCompliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => { loadData(); }, [propertyId]);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const propRes = await api.get(`/properties/${propertyId}`);
      setProperty(propRes.data);
      const compRes = await api.get(`/property-types/properties/${propertyId}/hmo-compliance`);
      setCompliance(compRes.data);
    } catch (err) { console.error('Failed to load:', err); }
    finally { setLoading(false); }
  };
  
  const handleUpdate = async (field, value) => {
    try {
      setSaving(true);
      await api.put(`/property-types/properties/${propertyId}/hmo-compliance`, { [field]: value });
      loadData();
    } catch (err) { console.error('Failed to update:', err); }
    finally { setSaving(false); }
  };
  
  if (loading) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;
  
  const issuesCount = compliance?.compliance_issues?.length || 0;
  
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-600 hover:text-primary mb-4"><ArrowLeft className="w-4 h-4" />Back</button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg"><Shield className="w-6 h-6 text-orange-600" /></div>
              <div><h1 className="text-xl font-bold">HMO Compliance</h1><p className="text-slate-600">{property?.address?.street}</p></div>
            </div>
            {issuesCount > 0 ? (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-red-100 text-red-700 rounded-full"><AlertTriangle className="w-4 h-4" /><span className="text-sm font-medium">{issuesCount} Issues</span></div>
            ) : (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full"><CheckCircle className="w-4 h-4" /><span className="text-sm font-medium">Compliant</span></div>
            )}
          </div>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">HMO License</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div><label className="block text-sm font-medium mb-2">License Required</label><div className={`p-3 rounded-lg ${compliance?.hmo_license_required ? 'bg-orange-50 text-orange-700' : 'bg-green-50 text-green-700'}`}>{compliance?.hmo_license_required ? 'Yes - License Required' : 'Not Required'}</div></div>
            <div><label className="block text-sm font-medium mb-2">License Status</label><select value={compliance?.license_status || 'unknown'} onChange={(e) => handleUpdate('license_status', e.target.value)} className="w-full border rounded-lg px-3 py-2"><option value="unknown">Unknown</option><option value="valid">Valid</option><option value="pending">Pending</option><option value="expired">Expired</option></select></div>
            <div><label className="block text-sm font-medium mb-2">License Number</label><input type="text" value={compliance?.license_number || ''} onChange={(e) => handleUpdate('license_number', e.target.value)} className="w-full border rounded-lg px-3 py-2" /></div>
            <div><label className="block text-sm font-medium mb-2">Max Occupants</label><input type="number" value={compliance?.max_occupants || ''} onChange={(e) => handleUpdate('max_occupants', parseInt(e.target.value) || null)} className="w-full border rounded-lg px-3 py-2" /></div>
          </div>
        </Card>
        
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4">Fire Safety</h2>
          <div className="space-y-4">
            <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"><span>Fire Risk Assessment</span><input type="checkbox" checked={compliance?.fire_risk_assessment || false} onChange={(e) => handleUpdate('fire_risk_assessment', e.target.checked)} className="w-5 h-5" /></label>
            <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"><span>Fire Doors Installed</span><input type="checkbox" checked={compliance?.fire_doors_installed || false} onChange={(e) => handleUpdate('fire_doors_installed', e.target.checked)} className="w-5 h-5" /></label>
            <label className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"><span>Emergency Lighting</span><input type="checkbox" checked={compliance?.emergency_lighting || false} onChange={(e) => handleUpdate('emergency_lighting', e.target.checked)} className="w-5 h-5" /></label>
          </div>
        </Card>
        
        {issuesCount > 0 && (
          <Card className="p-6 border-red-200 bg-red-50">
            <h2 className="text-lg font-semibold text-red-800 mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />Compliance Issues</h2>
            <ul className="space-y-2">{compliance.compliance_issues.map((issue, idx) => (<li key={idx} className="flex items-start gap-2 text-red-700"><XCircle className="w-4 h-4 mt-0.5" />{issue}</li>))}</ul>
          </Card>
        )}
        
        <div className="flex gap-4">
          <Link to={`/landlord/properties/${propertyId}/rooms`} className="flex-1"><Button variant="outline" className="w-full">Manage Rooms</Button></Link>
          <Button className="flex-1"><Upload className="w-4 h-4 mr-2" />Upload License</Button>
        </div>
      </div>
    </div>
  );
}
